'use client';

import React from 'react';
import { useMedia } from 'react-use';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { H2 } from '../WebsiteTypography';

const Orbit = ({ radius = 200, speed = 40, clockwise = false, images, isMobile }) => {
  const imageSize = isMobile ? 20 : 26;

  const styles = {
    circle: {
      width: `${radius * 2}px`,
      height: `${radius * 2}px`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px solid rgba(140, 69, 255, 0.6)',
      position: 'relative',
    },

    box: {
      backgroundColor: 'black',
      border: '1px solid #ffffff4d',
      borderRadius: '50%',
      width: isMobile ? 36 : 50,
      height: isMobile ? 36 : 50,
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  // Generate keyframes for each image
  const generateKeyframes = (index, totalImages) => {
    const startAngle = (360 / totalImages) * index;
    const endAngle = startAngle + 360;

    const name = `orbit${radius}_${index}${clockwise ? 'CW' : 'CCW'}`;

    return `
      @keyframes ${name} {
        from {
          transform: rotate(${clockwise ? startAngle : -startAngle}deg) 
                    translateY(${radius}px) 
                    rotate(${clockwise ? -startAngle : startAngle}deg);
        }
        to {
          transform: rotate(${clockwise ? endAngle : -endAngle}deg) 
                    translateY(${radius}px) 
                    rotate(${clockwise ? -endAngle : endAngle}deg);
        }
      }
    `;
  };

  // Generate all keyframes
  const keyframes = images.map((_, index) => generateKeyframes(index, images.length)).join('\n');

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <style>{keyframes}</style>
      <div style={styles.circle}>
        {images.map((image, index) => (
          <div
            key={index}
            style={{
              ...styles.box,
              animation: `orbit${radius}_${index}${clockwise ? 'CW' : 'CCW'} ${speed}s linear infinite`,
            }}
          >
            <Image
              src={image}
              width={imageSize}
              height={imageSize}
              style={{ objectFit: 'contain' }}
              alt={`Orbit item ${index + 1}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const Logo = ({ isMobile }) => {
  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <img
        src="https://cdn.welovealfa.com/alfa-logo-white.png"
        alt="Alfa Logo"
        style={{ height: isMobile ? 40 : 70 }}
      />
    </div>
  );
};

const ATS = () => {
  const isMobile = useMedia('(max-width: 768px)');

  const containerWidth = isMobile ? 300 : 600;
  const unit = containerWidth / 6;
  const orbitRadi = [unit, unit * 2, unit * 3];

  return (
    <div>
      <div
        style={{
          textAlign: 'center',
          paddingTop: 0,
          marginTop: 100,
          marginBottom: 20,
        }}
      >
        <H2>Integrates with your ATS</H2>
      </div>
      <div
        style={{
          textAlign: 'center',
          maxWidth: 750,
          margin: 'auto',
          fontSize: 16,
          color: '#ffffffba',
          marginBottom: 10,
          lineHeight: '26px',
        }}
      >
        Syncs candidate data with your applicant tracking system
        <br />
        <Link href="/ats" prefetch={true}>
          <span style={{ textDecoration: 'underline' }}>View list</span>
        </Link>
      </div>

      <div
        style={{
          width: containerWidth,
          height: containerWidth,
          position: 'relative',
          margin: 'auto',
          marginTop: isMobile ? 40 : 60,
        }}
      >
        <Logo isMobile={isMobile} />
        <Orbit
          isMobile={isMobile}
          radius={orbitRadi[0]}
          speed={80}
          images={[
            'https://cdn.welovealfa.com/GREENHOUSE_ICON_GREEN.png',
            'https://cdn.welovealfa.com/lever-icon.png',
          ]}
        />
        <Orbit
          isMobile={isMobile}
          radius={orbitRadi[1]}
          speed={60}
          clockwise
          images={[
            'https://cdn.welovealfa.com/workable-icon.png',
            'https://cdn.welovealfa.com/salesforce-logo.png',
            'https://cdn.welovealfa.com/hubspot-logo.png',
            'https://cdn.welovealfa.com/oracle-logo.png',
          ]}
        />
        <Orbit
          isMobile={isMobile}
          radius={orbitRadi[2]}
          speed={40}
          images={[
            'https://cdn.welovealfa.com/bullhorn-logo.png',
            'https://cdn.welovealfa.com/icims-logo.png',
            'https://cdn.welovealfa.com/workdaylogo.png',
            'https://cdn.welovealfa.com/zoho-recruit-logo.png',
          ]}
        />
      </div>
    </div>
  );
};

const ATSWrapper = dynamic(() => Promise.resolve(ATS), {
  ssr: false,
});

export default ATSWrapper;
